import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  DbQuestionData,
  DetailedPlayer,
  Player,
  VanquishedDemon,
} from '../components/room/types';
import { PoolPlayLeaderboard } from '../components/room/game/PoolPlayLeaderboard';
import { assignRanks, average } from '../components/room/utils';
import { ScorecardMulti } from '../components/room/game/scorecard-multi';
import CloseIcon from '@mui/icons-material/Close';
import { MmrResults } from '../dialog/MmrResults';
import { fetchGet } from '@par/shared/api/common';
import { StarterKitConfig } from '../components/starter-kits/types';
import { Mode } from '@par/shared/common/types';
import { Teams } from '@par/shared/types/game';
import { ToggleStats } from '../components/room/game/score-screen/ToggleStats';
import { ScoreCardStats } from '../components/room/game/score-screen/ScoreCardStats';

type PropTypes = {
  bannedCategories?: string[];
  categorySelections?: string[];
  categorySet?: string;
  demons?: boolean;
  demonsVanquished?: VanquishedDemon[];
  gameId: string;
  gameType?: 'casual' | 'pro';
  handicap?: number;
  isDaily?: boolean;
  league?: boolean;
  onClose: () => void;
  openScordcardModal: boolean;
  mode: Mode;
  players: Record<string, DetailedPlayer>;
  profileId: string;
  questionSet: DbQuestionData[];
  ranked: boolean;
  seededBy?: string;
  starterKit?: StarterKitConfig;
  survivalMode?: 'endurance' | 'challenge' | 'demon';
  teams?: Teams;
  variant?: 'normal' | 'survival' | 'starter-kit' | 'turbo';
};

export const ScoreCardModal = ({
  bannedCategories,
  categorySelections,
  categorySet,
  demons,
  demonsVanquished,
  gameId,
  isDaily,
  gameType,
  handicap,
  league,
  onClose,
  openScordcardModal,
  mode,
  players,
  profileId,
  questionSet,
  ranked,
  seededBy,
  starterKit,
  survivalMode,
  teams,
  variant,
}: PropTypes) => {
  const [sortedPlayers, setSortedPlayers] = useState<Player[]>();
  const [selectedPlayer, setSelectedPlayer] = useState<DetailedPlayer>();
  const [averageDifficulty, setAverageDifficulty] = useState<number>();
  const [openMmrResults, setOpenMmrResults] = useState(false);
  const [detailedPlayerSelf, setDetailedPlayerSelf] =
    useState<DetailedPlayer>();
  const [seedDetails, setSeedDetails] = useState<{
    submittedBy: string;
    submittedByRating: number;
  }>();
  const [toggleQuestionsStats, setToggleQuestionsStats] = useState<
    'scorecard' | 'stats'
  >('scorecard');

  useEffect(() => {
    if (!openScordcardModal) return;
    setToggleQuestionsStats('scorecard');
    setSortedPlayers(undefined);
    setAverageDifficulty(undefined);
    if (players[profileId]) {
      setSelectedPlayer(players[profileId]);
    } else {
      setSelectedPlayer(undefined);
    }
  }, [openScordcardModal]);

  useEffect(() => {
    if (!seededBy) return;
    fetchSeedDetails(gameId);
  }, [seededBy]);

  const sortPlayers = (players: Record<string, Player>) => {
    const sortedPlayers = Object.values(players).sort(
      (a, b) => a.parScore - b.parScore
    );
    return assignRanks(sortedPlayers);
  };

  useEffect(() => {
    if (mode === 'sit-n-go') {
      setSortedPlayers(sortPlayers(players));
    }
    setDetailedPlayerSelf(players[profileId]);
  }, [players]);

  useEffect(() => {
    if (!questionSet) return;
    const averageDifficulty = average(questionSet.map((q) => q.difficulty));
    setAverageDifficulty(averageDifficulty);
  }, [questionSet]);

  const handlePoolLeaderboardUsernameClick = (profileId: string) => {
    setSelectedPlayer(players[profileId]);
  };

  const fetchSeedDetails = async (gameId: string) => {
    const res = await fetchGet(`/match-making/seed-details/${gameId}`);
    setSeedDetails(res);
  };

  const handleShowStatsChange = (event: any, value: 'scorecard' | 'stats') => {
    if (value === null) return;
    setToggleQuestionsStats(value);
  };

  return (
    <>
      {detailedPlayerSelf ? (
        <MmrResults
          closeMmrResults={() => setOpenMmrResults(false)}
          detailedPlayers={players}
          mode={mode}
          openMmrResults={openMmrResults}
          seedDetails={seedDetails}
        />
      ) : null}
      <Dialog
        open={openScordcardModal}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            marginTop: '10px',
            marginRight: '10px',
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          {mode === 'teams' ? (
            <Stack
              direction={'row'}
              justifyContent={'center'}
              spacing={4}
              marginY={1}
            >
              <ToggleStats
                handleShowStatsChange={handleShowStatsChange}
                toggleQuestionsStats={toggleQuestionsStats}
              />
            </Stack>
          ) : null}
          {mode === 'teams' &&
          toggleQuestionsStats === 'stats' &&
          players &&
          teams ? (
            <Box mt={2}>
              <ScoreCardStats
                detailedPlayers={players}
                league={league}
                myTeam={teams.teamA}
                otherTeam={teams.teamB}
              />
            </Box>
          ) : null}
          {toggleQuestionsStats === 'scorecard' ? (
            <Stack direction="row" marginTop={3}>
              <ScorecardMulti
                averageDifficulty={averageDifficulty}
                bannedCategories={bannedCategories}
                categorySelections={categorySelections}
                categorySet={categorySet}
                demons={demons}
                demonsVanquished={demonsVanquished}
                detailedPlayers={players}
                gameId={gameId}
                gameType={gameType}
                handicap={handicap}
                handleMmrResultsClick={() => setOpenMmrResults(true)}
                hideTitle={mode === 'teams'}
                isDaily={isDaily}
                league={league}
                mode={mode}
                myTeam={teams?.teamA}
                otherTeam={teams?.teamB}
                profileId={profileId}
                questionSet={questionSet}
                ranked={ranked}
                seededBy={seededBy}
                selectedPlayer={selectedPlayer}
                showDislike={!!players[profileId]}
                showRatings={!!players[profileId]}
                showMmrResults={!!seededBy && !!players[profileId]}
                starterKit={starterKit}
                survivalMode={survivalMode}
                variant={variant}
              />
              {mode === 'sit-n-go' ? (
                <Stack>
                  <Typography
                    variant="h4"
                    textAlign="center"
                    letterSpacing={3}
                    sx={{ display: { xs: 'none', sm: 'block' } }}
                  >
                    LEADERBOARD
                  </Typography>
                  <Typography
                    variant="h6"
                    textAlign="center"
                    letterSpacing={2}
                    sx={{ display: { xs: 'block', sm: 'none' } }}
                  >
                    LEADERBOARD
                  </Typography>
                  <Typography
                    variant={'caption'}
                    textAlign={'center'}
                    letterSpacing={3}
                  >
                    {`Total Players: ${Object.values(players).length}`}
                  </Typography>
                  <Typography
                    variant={'caption'}
                    textAlign={'center'}
                    color="warning.main"
                    marginTop={1}
                  >
                    Click player to view their scores
                  </Typography>
                  <PoolPlayLeaderboard
                    gameOver={true}
                    modalView={true}
                    onUsernameClick={handlePoolLeaderboardUsernameClick}
                    sortedPlayers={sortedPlayers}
                    profileId={profileId}
                    roundOver={true}
                  ></PoolPlayLeaderboard>
                </Stack>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
